import React, { useEffect, useState } from 'react';

export interface TimeCounterProps {
  startTime: Date | string;
  showSeconds?: boolean;
  warningThresholds?: {
    yellow?: number; // seconds
    red?: number; // seconds
  };
  onThresholdReached?: (threshold: 'yellow' | 'red', elapsedSeconds: number) => void;
  className?: string;
  prefix?: string;
  suffix?: string;
  countdownDuration?: number; // seconds - if provided, shows countdown instead of elapsed time
}

export const TimeCounter: React.FC<TimeCounterProps> = ({
  startTime,
  showSeconds = true,
  warningThresholds,
  onThresholdReached,
  className = '',
  prefix = '',
  suffix = '',
  countdownDuration,
}) => {
  const [displayTime, setDisplayTime] = useState<number>(0);
  const [currentThreshold, setCurrentThreshold] = useState<'normal' | 'yellow' | 'red'>('normal');

  useEffect(() => {
    const startDate = typeof startTime === 'string' ? new Date(startTime) : startTime;

    const updateTime = () => {
      const now = new Date();
      const elapsed = Math.floor((now.getTime() - startDate.getTime()) / 1000);

      // If countdown mode, calculate remaining time
      const timeToDisplay = countdownDuration ? Math.max(0, countdownDuration - elapsed) : elapsed;
      setDisplayTime(timeToDisplay);

      // Check thresholds (always based on elapsed time)
      if (warningThresholds) {
        const newThreshold =
          warningThresholds.red && elapsed >= warningThresholds.red
            ? 'red'
            : warningThresholds.yellow && elapsed >= warningThresholds.yellow
            ? 'yellow'
            : 'normal';

        if (newThreshold !== currentThreshold) {
          setCurrentThreshold(newThreshold);
          if (newThreshold !== 'normal' && onThresholdReached) {
            onThresholdReached(newThreshold, elapsed);
          }
        }
      }
    };

    // Initial calculation
    updateTime();

    // Update every second
    const interval = setInterval(updateTime, 1000);

    return () => clearInterval(interval);
  }, [startTime, warningThresholds, currentThreshold, onThresholdReached, countdownDuration]);

  const formatTime = (totalSeconds: number): string => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    if (showSeconds) {
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }

    if (minutes === 0) {
      return '< 1 min';
    }

    return `${minutes} min`;
  };

  const getColorClass = (): string => {
    switch (currentThreshold) {
      case 'red':
        return 'text-red-600 font-bold';
      case 'yellow':
        return 'text-yellow-600 font-semibold';
      default:
        return 'text-gray-600';
    }
  };

  const getBadgeClass = (): string => {
    switch (currentThreshold) {
      case 'red':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'yellow':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  return (
    <span
      className={`inline-flex items-center px-2 py-1 rounded text-xs border ${getBadgeClass()} ${className}`}
      title={`Started at: ${typeof startTime === 'string' ? startTime : startTime.toLocaleString()}`}>
      {prefix && <span className="mr-1">{prefix}</span>}
      <span className={getColorClass()}>{formatTime(displayTime)}</span>
      {suffix && <span className="ml-1">{suffix}</span>}
      {currentThreshold === 'red' && <span className="ml-1">⏰</span>}
      {currentThreshold === 'yellow' && <span className="ml-1">⚠️</span>}
    </span>
  );
};
